/**
 * InlineEditable — édition directe au clic (texte, zone de texte, sélection, nombre).
 * Clic (ou Entrée) = édition ; Entrée/blur = enregistre ; Échap = annule. Indicateur « ✓ » bref.
 * Éditeurs structurés associés : TagsEditor (tags colorés + autocomplétion), CepagesEditor (cépages + %).
 */
import { useState, useEffect } from 'react';
import { Pencil, Check, X } from 'lucide-react';

export function InlineEditable({
  value, onSave, type = 'text', options = [], placeholder = '—',
  displayClassName = '', inputClassName = '', testId = 'inline',
}) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value ?? '');
  const [saved, setSaved] = useState(false);
  useEffect(() => setVal(value ?? ''), [value]);

  const flash = () => { setSaved(true); setTimeout(() => setSaved(false), 1500); };
  const commit = async () => {
    setEditing(false);
    if ((val ?? '') !== (value ?? '')) { await onSave(type === 'number' ? (val === '' ? null : Number(val)) : val); flash(); }
  };
  const cancel = () => { setVal(value ?? ''); setEditing(false); };

  if (type === 'select') {
    return (
      <span className="inline-flex items-center gap-1">
        <select className={`vd-input h-8 ${inputClassName}`} value={value ?? ''} data-testid={`${testId}-select`}
          onChange={async (e) => { await onSave(e.target.value); flash(); }}>
          {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        {saved && <Check className="w-3.5 h-3.5 text-gold700" />}
      </span>
    );
  }

  if (!editing) {
    return (
      <span role="button" tabIndex={0} data-testid={`${testId}-display`}
        onClick={() => setEditing(true)} onKeyDown={(e) => { if (e.key === 'Enter') setEditing(true); }}
        className={`group inline-flex items-start gap-1 cursor-text rounded px-0.5 hover:outline hover:outline-1 hover:outline-dashed hover:outline-gold ${displayClassName}`}>
        <span className={!value ? 'text-n500' : ''}>{value || placeholder}</span>
        <Pencil className="w-3 h-3 mt-1 shrink-0 opacity-0 group-hover:opacity-60 text-gold700" />
        {saved && <Check className="w-3 h-3 mt-1 text-gold700" />}
      </span>
    );
  }

  const props = {
    autoFocus: true, value: val, onChange: (e) => setVal(e.target.value), onBlur: commit,
    className: `vd-input ${inputClassName}`, 'data-testid': `${testId}-input`,
  };
  if (type === 'textarea') {
    return <textarea rows={4} {...props}
      onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) commit(); if (e.key === 'Escape') cancel(); }} />;
  }
  return <input type={type === 'number' ? 'number' : 'text'} {...props}
    onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') cancel(); }} />;
}

/** Édition des tags d'une cuvée : chips colorées + autocomplétion + création à la volée. */
export function TagsEditor({ tags = [], suggestions = [], onChange }) {
  const [input, setInput] = useState('');
  const [open, setOpen] = useState(false);
  const colorOf = (name) => (suggestions.find((s) => s.name === name) || {}).color || '#b68235';
  const add = (name) => { const n = (name || '').trim(); if (!n || tags.includes(n)) return; onChange([...tags, n]); setInput(''); setOpen(false); };
  const remove = (name) => onChange(tags.filter((t) => t !== name));
  const matches = suggestions.filter((s) => s.name.toLowerCase().includes(input.toLowerCase()) && !tags.includes(s.name)).slice(0, 6);

  return (
    <div className="flex flex-wrap gap-1.5 items-center" data-testid="tags-editor">
      {tags.map((t) => (
        <span key={t} className="vd-tag inline-flex items-center gap-1" style={{ borderColor: colorOf(t), color: colorOf(t) }} data-testid={`tag-${t}`}>
          <span className="w-2 h-2 rounded-full" style={{ background: colorOf(t) }} />{t}
          <button onClick={() => remove(t)} aria-label={`Retirer ${t}`} data-testid={`tag-remove-${t}`}><X className="w-3 h-3" /></button>
        </span>
      ))}
      <div className="relative">
        <input value={input} onChange={(e) => { setInput(e.target.value); setOpen(true); }} placeholder="+ tag"
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(input); } }}
          onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
          className="vd-input h-7 w-24 text-[12px]" data-testid="tag-input" />
        {open && (input || matches.length > 0) && (
          <div className="absolute z-20 mt-1 bg-paper border border-hair rounded shadow-md w-44 max-h-48 overflow-auto">
            {matches.map((m) => (
              <button key={m.name} onMouseDown={() => add(m.name)} className="flex items-center gap-2 w-full text-left px-2 py-1.5 text-[13px] hover:bg-surface">
                <span className="w-2 h-2 rounded-full" style={{ background: m.color }} />{m.name}
              </button>
            ))}
            {input.trim() && !suggestions.some((s) => s.name === input.trim()) && (
              <button onMouseDown={() => add(input)} className="w-full text-left px-2 py-1.5 text-[13px] hover:bg-surface text-gold700" data-testid="tag-create">Créer « {input.trim()} »</button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/** Édition des cépages : liste nom + pourcentage, ajout avec suggestions. */
export function CepagesEditor({ cepages = [], suggestions = [], onChange }) {
  const [input, setInput] = useState('');
  const norm = (c) => (typeof c === 'string' ? { nom: c, pct: null } : c);
  const list = cepages.map(norm);
  const add = (nom) => { const n = (nom || '').trim(); if (!n || list.some((c) => c.nom === n)) return; onChange([...list, { nom: n, pct: null }]); setInput(''); };
  const setPct = (i, pct) => onChange(list.map((c, idx) => (idx === i ? { ...c, pct: pct === '' ? null : Number(pct) } : c)));
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
  const matches = suggestions.filter((s) => s.toLowerCase().includes(input.toLowerCase()) && !list.some((c) => c.nom === s)).slice(0, 6);

  return (
    <div data-testid="cepages-editor">
      <div className="space-y-1">
        {list.map((c, i) => (
          <div key={i} className="flex items-center gap-2 text-[13px]">
            <span className="flex-1">{c.nom}</span>
            <input type="number" value={c.pct ?? ''} onChange={(e) => setPct(i, e.target.value)} placeholder="%" className="vd-input h-7 w-16 text-[12px] nums" data-testid={`cepage-pct-${i}`} />
            <button onClick={() => remove(i)} aria-label={`Retirer ${c.nom}`} data-testid={`cepage-remove-${i}`}><X className="w-3.5 h-3.5" /></button>
          </div>
        ))}
      </div>
      <div className="relative mt-1.5">
        <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="+ cépage" className="vd-input h-7 text-[12px]" data-testid="cepage-input"
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(input); } }} />
        {input && matches.length > 0 && (
          <div className="absolute z-20 mt-1 bg-paper border border-hair rounded shadow-md w-full max-h-48 overflow-auto">
            {matches.map((m) => <button key={m} onMouseDown={() => add(m)} className="block w-full text-left px-2 py-1.5 text-[13px] hover:bg-surface">{m}</button>)}
          </div>
        )}
      </div>
    </div>
  );
}
